/**
 * Feed Administration Store
 *
 * Manages feed-administration, quick-feed, and re-dosing records.
 */
import { defineStore } from 'pinia';
import i18next from 'i18next';
import { getApiErrorMessage } from '@/core/utils/api-error.util';
import type {
  FeedRecord,
  QuickFeedRecord,
  ReDosingRecord,
  FeedAdministrationTab,
  ApiFeedDeliveryRecord,
  ApiQuickFeedRecord,
  ApiReDosingRecord,
} from '@/modules/feed-administration/types';
import {
  fetchFeedRecords,
  fetchQuickFeedRecords,
  fetchReDosingRecords,
} from '@/modules/feed-administration/services/feed-administration.api';
import { formatDateTime as _fmtDt, formatDate as _fmtD } from '@tms-phoenix/ui-vue/utils/datetime.util';

export type FeedAdministrationQueryParams = {
  tab?: FeedAdministrationTab;
  search?: string;
};

/** Resolve the user's IANA timezone from the auth store, fallback to browser TZ. */
/** Returns the browser's system timezone. */
function getUserTz(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

function apiToFeedRecord(r: ApiFeedDeliveryRecord): FeedRecord {
  const volumeQty = r.volume_ml != null ? `${parseFloat(r.volume_ml)} ${i18next.t('common.milkUnit')}` : '—';
  return {
    id: r.delivery_id,
    patientId: r.patient_id ?? null,
    patientName: r.patient_name,
    mrn: r.mrn,
    feedId: r.feed_id,
    orderIdentifier: r.order_identifier ?? null,
    containerIdentifier: r.container_identifier ?? '—',
    feedTime: r.feed_time ? _fmtDt(r.feed_time, getUserTz()) : '—',
    orderDate: r.order_date ? _fmtD(r.order_date, getUserTz()) : '—',
    volumeQty,
    status: r.status,
    tab: 'feed-administration',
  };
}

function apiToQuickFeedRecord(r: ApiQuickFeedRecord): QuickFeedRecord {
  const patientName = r.patient
    ? `${r.patient.first_name} ${r.patient.last_name}`
    : '';
  return {
    id: r.prep_id,
    patientId: r.patient_id,
    patientName,
    mrn: r.patient?.mrn ?? '',
    containerId: r.container_identifier,
    motherName: r.mother_name,
    date: r.created_at ? _fmtD(r.created_at, getUserTz()) : '',
  };
}

function apiToReDosingRecord(r: ApiReDosingRecord): ReDosingRecord {
  return {
    id: r.prep_id,
    patientId: r.patient_id ?? null,
    patientName: r.patient_name,
    mrn: r.mrn,
    feedId: r.container_identifier ?? r.prep_id,
    doseCreated: r.number_of_doses,
  };
}

export const useFeedAdministrationStore = defineStore('feedAdministration', {
  state: () => ({
    records: [] as FeedRecord[],
    quickFeedRecords: [] as QuickFeedRecord[],
    reDosingRecords: [] as ReDosingRecord[],
    isLoading: false,
    error: null as string | null,
    activeTab: 'feed-administration' as FeedAdministrationTab,
    searchQuery: '',
  }),

  getters: {
    /**
     * Client-side filtered feed-administration records.
     *
     * GET /feedAdmin/deliveries (DeliveryListQuerySchema) only accepts
     * `hospital_id` — the backend has no search/filter query params, so
     * matching happens here against every column rendered in
     * FeedAdministrationTable (patient name, MRN, order ID, container
     * barcode, feed time, order date, volume/qty).
     */
    filteredRecords(state): FeedRecord[] {
      const q = state.searchQuery.trim().toLowerCase();
      if (!q) return state.records;
      return state.records.filter(
        (r: FeedRecord) =>
          r.patientName.toLowerCase().includes(q) ||
          r.mrn.toLowerCase().includes(q) ||
          (r.orderIdentifier ?? '').toLowerCase().includes(q) ||
          r.containerIdentifier.toLowerCase().includes(q) ||
          r.feedTime.toLowerCase().includes(q) ||
          r.orderDate.toLowerCase().includes(q) ||
          r.volumeQty.toLowerCase().includes(q),
      );
    },

    /**
     * Client-side filtered quick-feed records.
     *
     * GET /quickFeeds (QuickFeedListQuerySchema) accepts optional
     * `patient_id`, `status`, and `patient_name` filters alongside the
     * required `hospital_id` — but setSearch() skips re-fetching for this
     * tab, so the full list is always resident client-side. Match against
     * every column rendered in QuickFeedTable (patient name, MRN, container
     * ID, mother name, date) instead of relying on the unused backend params.
     */
    filteredQuickFeedRecords(state): QuickFeedRecord[] {
      const q = state.searchQuery.trim().toLowerCase();
      if (!q) return state.quickFeedRecords;
      return state.quickFeedRecords.filter(
        (r: QuickFeedRecord) =>
          r.patientName.toLowerCase().includes(q) ||
          r.mrn.toLowerCase().includes(q) ||
          (r.containerId ?? '').toLowerCase().includes(q) ||
          (r.motherName ?? '').toLowerCase().includes(q) ||
          r.date.toLowerCase().includes(q),
      );
    },

    /**
     * Client-side filtered re-dosing records.
     *
     * GET /reDosing (ReDosingListQuerySchema) only accepts `hospital_id` — no
     * search/filter query params — so matching happens here against every
     * column rendered in ReDosingTable (patient name, MRN, container ID,
     * dose count).
     */
    filteredReDosingRecords(state): ReDosingRecord[] {
      const q = state.searchQuery.trim().toLowerCase();
      if (!q) return state.reDosingRecords;
      return state.reDosingRecords.filter(
        (r: ReDosingRecord) =>
          r.patientName.toLowerCase().includes(q) ||
          r.mrn.toLowerCase().includes(q) ||
          r.feedId.toLowerCase().includes(q) ||
          String(r.doseCreated).toLowerCase().includes(q),
      );
    },
  },

  actions: {
    setTab(tab: FeedAdministrationTab) {
      this.activeTab = tab;
      this.fetchRecords();
    },

    /**
     * Switch the active tab and refetch, preserving whatever search text is
     * currently set (component callers may sync a persisted value into
     * `searchQuery` beforehand) so the table stays in sync with the
     * SearchBar across tab switches and page navigation.
     */
    switchTab(tab: FeedAdministrationTab) {
      this.activeTab = tab;
      this.fetchRecords();
    },

    setSearch(query: string) {
      this.searchQuery = query;
      // Re-dosing and quick-feed are filtered client-side via their respective
      // getters — skip the network call and let the getter re-evaluate reactively.
      if (this.activeTab === 're-dosing' || this.activeTab === 'quick-feed') return;
      this.fetchRecords();
    },

    /**
     * Reset the search query without triggering a fetch. Call this whenever
     * the view (re)mounts so a fresh visit to this module never inherits a
     * stale filter left over from a previous visit/session (the store is a
     * singleton and outlives route navigation, unlike component-local refs).
     */
    resetSearch() {
      this.searchQuery = '';
    },

    async fetchRecords() {
      this.isLoading = true;
      this.error = null;
      try {
        const search = this.searchQuery || undefined;
        if (this.activeTab === 'feed-administration') {
          const response = await fetchFeedRecords();
          const items = Array.isArray(response.data) ? response.data : [];
          this.records = items.length > 0 ? items.map(apiToFeedRecord) : [];
        } else if (this.activeTab === 'quick-feed') {
          const response = await fetchQuickFeedRecords({
            patient_name: search,
          });
          const items = Array.isArray(response.data) ? response.data : [];
          this.quickFeedRecords = items.map(apiToQuickFeedRecord);
        } else {
          const response = await fetchReDosingRecords();
          const items = Array.isArray(response.data) ? response.data : [];
          this.reDosingRecords = items.length > 0 ? items.map(apiToReDosingRecord) : [];
        }
      } catch (err) {
        console.error('[FeedAdminStore] fetchRecords failed', err);
        if (this.activeTab === 'feed-administration') {
          this.records = [];
        } else if (this.activeTab === 'quick-feed') {
          this.quickFeedRecords = [];
        } else {
          this.reDosingRecords = [];
        }
        this.error = getApiErrorMessage(err, 'Failed to fetch records');
      } finally {
        this.isLoading = false;
      }
    }
  },
});
